/* Provides shared feed items (contentType "all") to FeedPage and SwipePage.
   Uses paginated list query with index-based starred-first ordering. */

import { useCallback, useMemo, useState, type ReactNode } from "react";
import { usePaginatedQuery, useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { FeedItemsContext } from "@/contexts/feedItemsContextValue";
import { useUserSettings } from "@/hooks/useUserSettings";
import { useSectionGroupings } from "@/hooks/useSectionGroupings";
import { useKiwiReward } from "@/hooks/useKiwiReward";
import type { KiwiSkinConfig } from "@/types/kiwiSkin";
import { mapEnrichedFeedItem } from "@/lib/mapEnrichedFeedItem";
import { posthog } from "@/lib/posthog";
import { Events } from "@/lib/posthogEvents";

const INITIAL_NUM_ITEMS = 30;

interface FeedItemsProviderProps {
  children: ReactNode;
}

export function FeedItemsProvider({ children }: FeedItemsProviderProps) {
  const { hideProvocativeContent } = useUserSettings();
  const { sectionsEnabled, sectionGroupings } = useSectionGroupings();
  const { triggerReward } = useKiwiReward();
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const useSectionPagination = sectionsEnabled && sectionGroupings.length > 0;

  const standardQuery = usePaginatedQuery(
    api.feedItems.list,
    useSectionPagination
      ? "skip"
      : { contentType: "all", hideProvocative: hideProvocativeContent, sortOrder },
    { initialNumItems: INITIAL_NUM_ITEMS },
  );

  const sectionQueryArgs = useMemo(
    () => ({
      contentType: "all",
      hideProvocative: hideProvocativeContent,
      sectionPagination: true,
      sectionGroupings,
      sortOrder,
    }),
    [hideProvocativeContent, sectionGroupings, sortOrder],
  );

  const sectionOrderedQuery = usePaginatedQuery(
    api.feedItems.list,
    useSectionPagination ? (sectionQueryArgs as any) : "skip",
    { initialNumItems: INITIAL_NUM_ITEMS },
  );

  const results = useSectionPagination
    ? sectionOrderedQuery.results
    : standardQuery.results;
  const status = useSectionPagination
    ? sectionOrderedQuery.status
    : standardQuery.status;
  const loadMore = useSectionPagination
    ? sectionOrderedQuery.loadMore
    : standardQuery.loadMore;

  const allCounts = useQuery(api.feedItems.getAllCounts);
  const totalCount = allCounts?.feed.sidebarCount ?? undefined;
  const provocativeCount = allCounts?.feed.provocativeCount ?? 0;
  const remove = useMutation(api.feedItems.remove);
  const complete = useMutation(api.feedItems.markAsCompleted);
  const queue = useMutation(api.feedItems.moveToQueue);
  const backlog = useMutation(api.feedItems.moveToBacklog);

  const completeWithReward = useCallback(async (id: Id<"feedItems">) => {
    const result = await complete({ id });
    const awarded = (result as { awardedSkin?: KiwiSkinConfig } | undefined)?.awardedSkin;
    if (awarded) triggerReward(awarded);
  }, [complete, triggerReward]);

  const items = results.map((raw) =>
    mapEnrichedFeedItem(raw as unknown as { _id: string; [key: string]: unknown }),
  );

  return (
    <FeedItemsContext
      value={{
        items,
        totalCount,
        provocativeCount,
        isFirstLoading: status === "LoadingFirstPage",
        hasMore: status === "CanLoadMore",
        isLoadingMore: status === "LoadingMore",
        loadMore,
        removeItem: (id: Id<"feedItems">) => { remove({ id }); posthog.capture(Events.ITEM_DELETED, { itemId: id, page: "feed" }); },
        completeItem: (id: Id<"feedItems">) => { completeWithReward(id); posthog.capture(Events.ITEM_COMPLETED, { itemId: id, page: "feed" }); },
        queueItem: (id: Id<"feedItems">) => { queue({ id }); posthog.capture(Events.ITEM_QUEUED, { itemId: id, page: "feed" }); },
        backlogItem: (id: Id<"feedItems">) => { backlog({ id }); posthog.capture(Events.ITEM_SHELVED, { itemId: id, page: "feed" }); },
        sortOrder,
        setSortOrder,
      }}
    >
      {children}
    </FeedItemsContext>
  );
}
